/**
 * Phase 308 — Staff Timesheets Dashboard
 * =======================================
 *
 * Owner-facing roster view powered by GET /api/staff/attendance/list.
 * Filters by status / date range / staff member; one-tap CSV export
 * for the accountant.
 *
 * Render conventions match the rest of the admin app: Shadcn Card +
 * Table + Tailwind + lucide icons.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Clock, Download, Filter, RefreshCw, Timer, Users, Search,
} from 'lucide-react';
import { toast } from 'sonner';

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch (_) { return iso; }
}

function formatDuration(minutes) {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function monthAgoISO() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

/**
 * Convert the in-memory rows array to a CSV Blob and trigger a download.
 * Pure client-side — no backend roundtrip. Compatible with Excel /
 * Google Sheets / Tally direct paste.
 */
function downloadShiftsAsCSV(rows, fileName = 'staff-timesheets.csv') {
  if (!rows || rows.length === 0) {
    toast.error('No rows to export');
    return;
  }
  const cols = [
    { key: 'staff_name', label: 'Staff Name' },
    { key: 'staff_id', label: 'Staff ID' },
    { key: 'status', label: 'Status' },
    { key: 'clock_in_time', label: 'Clock In' },
    { key: 'clock_out_time', label: 'Clock Out' },
    { key: 'total_minutes', label: 'Minutes' },
    { key: 'total_hours', label: 'Hours' },
    { key: 'clock_in_distance_m', label: 'Clock-in Distance (m)' },
    { key: 'clock_out_distance_m', label: 'Clock-out Distance (m)' },
    { key: 'ping_count', label: 'Ping Count' },
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const header = cols.map(c => escape(c.label)).join(',');
  const lines = rows.map(r => {
    const enriched = { ...r, total_hours: ((Number(r.total_minutes) || 0) / 60).toFixed(2) };
    return cols.map(c => escape(enriched[c.key])).join(',');
  });
  const csv = [header, ...lines].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`Exported ${rows.length} shifts to CSV`);
}

function StatusBadge({ status }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-semibold" data-testid="status-active">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        Active
      </span>
    );
  }
  return (
    <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]" data-testid="status-completed">
      Completed
    </Badge>
  );
}

function MetricTile({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-2xl px-5 py-4 shadow-sm" data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-500">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="mt-2 text-3xl font-bold tabular-nums text-slate-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-400">{sub}</div> : null}
    </div>
  );
}

export default function StaffTimesheets() {
  const [shifts, setShifts] = useState([]);
  const [totalMinutes, setTotalMinutes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(monthAgoISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [searchName, setSearchName] = useState('');

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const { data } = await api.get(`/staff/attendance/list?${params.toString()}`);
      setShifts(data?.shifts || []);
      setTotalMinutes(data?.total_minutes || 0);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) {
        toast.error('Only academy owners can view timesheets.');
      } else {
        toast.error('Failed to load timesheets.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  // Client-side name search on top of the server-filtered set
  const filteredShifts = useMemo(() => {
    const q = searchName.trim().toLowerCase();
    if (!q) return shifts;
    return shifts.filter(s => (s.staff_name || '').toLowerCase().includes(q));
  }, [shifts, searchName]);

  const uniqueStaff = useMemo(() => {
    const ids = new Set();
    shifts.forEach(s => { if (s.staff_id) ids.add(s.staff_id); });
    return ids.size;
  }, [shifts]);

  const activeShiftsCount = useMemo(
    () => shifts.filter(s => s.status === 'active').length,
    [shifts],
  );

  if (loading) {
    return (
      <div className="p-8 space-y-5" data-testid="staff-timesheets-loading">
        <Skeleton className="h-9 w-72" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-7xl mx-auto" data-testid="staff-timesheets-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Clock className="w-7 h-7 text-emerald-600" />
            Staff Timesheets
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Geofence-verified clock-in/out logs. Payroll-ready CSV export, one tap.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setRefreshing(true); load(); }}
            disabled={refreshing}
            data-testid="timesheets-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => downloadShiftsAsCSV(filteredShifts, `timesheets-${dateFrom}-to-${dateTo}.csv`)}
            disabled={filteredShifts.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="timesheets-export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1.5" />
            Export to Payroll (CSV)
          </Button>
        </div>
      </div>

      {/* Metric tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricTile
          icon={Clock}
          label="Total Hours"
          value={formatDuration(totalMinutes)}
          sub={`${shifts.length} shift${shifts.length === 1 ? '' : 's'} in range`}
          testId="metric-total-hours"
        />
        <MetricTile
          icon={Users}
          label="Unique Staff"
          value={uniqueStaff}
          sub="Distinct people clocked in"
          testId="metric-unique-staff"
        />
        <MetricTile
          icon={Timer}
          label="Currently Active"
          value={activeShiftsCount}
          sub="Coaches on the clock now"
          testId="metric-active-shifts"
        />
        <MetricTile
          icon={Filter}
          label="Period"
          value={`${dateFrom.slice(5)} → ${dateTo.slice(5)}`}
          sub="Adjust filters below"
          testId="metric-period"
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="mt-1 h-9 text-sm"
                data-testid="timesheets-date-from"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="mt-1 h-9 text-sm"
                data-testid="timesheets-date-to"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="timesheets-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active (clocked in)</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Search</Label>
              <div className="relative mt-1">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="Staff name…"
                  className="h-9 pl-8 text-sm"
                  data-testid="timesheets-search"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card data-testid="timesheets-table-card">
        <CardHeader className="border-b pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            Shifts
            <Badge variant="outline" className="ml-2 text-xs">
              {filteredShifts.length} row{filteredShifts.length === 1 ? '' : 's'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredShifts.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-slate-500" data-testid="timesheets-empty-state">
              <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              No shifts in the selected window. Adjust filters or wait for coaches to ping in.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff</TableHead>
                  <TableHead className="hidden md:table-cell">Status</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredShifts.map(s => (
                  <TableRow key={s.id} data-testid={`timesheet-row-${s.id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-800">{s.staff_name || '—'}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{(s.staff_id || '').slice(0, 8)}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <StatusBadge status={s.status} />
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="text-slate-700">{formatDateTime(s.clock_in_time)}</div>
                      {s.clock_in_distance_m !== undefined && s.clock_in_distance_m !== null ? (
                        <div className="text-[10px] text-slate-400">{Math.round(s.clock_in_distance_m)}m from pin</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-sm">
                      {s.clock_out_time ? (
                        <>
                          <div className="text-slate-700">{formatDateTime(s.clock_out_time)}</div>
                          {s.clock_out_distance_m !== undefined && s.clock_out_distance_m !== null ? (
                            <div className="text-[10px] text-slate-400">{Math.round(s.clock_out_distance_m)}m from pin</div>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-emerald-600 text-xs font-semibold">Still on duty</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold text-slate-800 tabular-nums">
                      {s.status === 'active' ? (
                        <span className="text-emerald-600 text-xs">in progress</span>
                      ) : (
                        formatDuration(s.total_minutes)
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <p className="text-center text-xs text-slate-400 pt-3">
        Hours auto-calculated from GPS pings every 5 minutes — 2-strike drift buffer prevents false clock-outs.
      </p>
    </div>
  );
}
